import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { SingleSelect } from 'dibs-elements/exports/SingleSelect';
import get from 'lodash.get';
import { addAndApplyFilter } from '../../../actions/filterActions';
import { MAX_DISPLAY_LOCATIONS } from './sbRespTradeOnApprovalPromoConstants';
import {
    getUSCities,
    getUSLocation,
    getUSLocations,
    mapLocationToOption,
    insertItemAt,
} from './sbRespTradeOnApprovalPromoHelpers';

import style from './SbRespTradeOnApprovalPromoLocationFilter.scss';

const messages = defineMessages({
    placeholder: {
        id: 'abt.SbRespTradeOnApprovalPromoLocationFilter.placeholder',
        defaultMessage: 'Browse by City (Optional)',
    },
    allUSLabel: {
        id: 'abt.SbRespTradeOnApprovalPromoLocationFilter.allUSLabel',
        defaultMessage: 'All United States',
    },
});

function buildAllOptions({ locations }) {
    return locations.map(mapLocationToOption);
}

function buildTopOptions({ locations, intl }) {
    const usLocation = getUSLocation({ locations });
    const allUSOption = {
        label: intl.formatMessage(messages.allUSLabel),
        value: get(usLocation, 'label'),
    };
    const topCities = getUSCities({ locations });
    return topCities
        .slice(0, MAX_DISPLAY_LOCATIONS)
        .map(mapLocationToOption)
        .concat([allUSOption]);
}

export class SbRespTradeOnApprovalPromoLocationFilterComponent extends React.Component {
    constructor(props) {
        super(props);

        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onInputChange = this.onInputChange.bind(this);
        this.onLocationSelect = this.onLocationSelect.bind(this);
        this.buildDisplayOptions = this.buildDisplayOptions.bind(this);
        this.handleSearchingStateChange = this.handleSearchingStateChange.bind(this);

        const locationFacetResult = get(props, 'searchBrowse.facets.location') || [];
        const locations = getUSLocations({ locations: locationFacetResult });
        const topOptions = buildTopOptions({ intl: props.intl, locations });

        this.state = {
            isSearching: false,
            selectedOption: null,
            allOptions: buildAllOptions({ locations }),
            displayOptions: topOptions,
            topOptions: topOptions,
        };
    }

    onInputChange(inputValue) {
        const isSearching = !!inputValue;
        if (this.state.isSearching !== isSearching) {
            this.handleSearchingStateChange({ isSearching });
        }
    }

    onChange(option) {
        this.setState(
            {
                isSearching: false,
                selectedOption: option || null,
                displayOptions: this.buildDisplayOptions(option),
            },
            this.onLocationSelect
        );
    }

    onBlur(option) {
        if (!option) {
            this.handleSearchingStateChange({ isSearching: false });
        }
    }

    handleSearchingStateChange({ isSearching }) {
        const displayOptions = isSearching ? this.state.allOptions : this.state.topOptions;
        this.setState({ isSearching, displayOptions });
    }

    onLocationSelect() {
        const { selectedOption } = this.state;
        if (selectedOption) {
            const isFromTypeahead = true;
            const filterName = 'location';
            const filterPayload = {
                urlLabel: selectedOption.value,
            };
            this.props.dispatch(addAndApplyFilter(filterName, filterPayload, isFromTypeahead));
        }
    }

    buildDisplayOptions(option) {
        const { topOptions } = this.state;
        if (option) {
            const isSelectedFromTop = topOptions.find(o => o.value === option.value);
            return isSelectedFromTop
                ? topOptions
                : insertItemAt(topOptions, option, topOptions.length - 1);
        }
        return topOptions;
    }

    render() {
        const { intl } = this.props;
        const { value } = this.state.selectedOption || {};
        return (
            <div className={style.selectContainer}>
                <SingleSelect
                    searchable
                    clearable={this.state.isSearching || !!this.state.selectedOption}
                    options={this.state.displayOptions}
                    dataTn="us-locations-search"
                    name="us-locations-search"
                    noResultsPrompt={
                        <FormattedMessage
                            id="abt.SbRespTradeOnApprovalPromoLocationFilter.noResults"
                            defaultMessage="No Results Found"
                        />
                    }
                    onBlur={this.onBlur}
                    onChange={this.onChange}
                    onInputChange={this.onInputChange}
                    placeholder={intl.formatMessage(messages.placeholder)}
                    value={value}
                    size="medium"
                />
            </div>
        );
    }
}

SbRespTradeOnApprovalPromoLocationFilterComponent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object,
    relay: PropTypes.object,
    searchBrowse: PropTypes.object,
};

export const SbRespTradeOnApprovalPromoLocationFilter = createFragmentContainer(
    injectIntl(connect()(SbRespTradeOnApprovalPromoLocationFilterComponent)),
    {
        searchBrowse: graphql`
            fragment SbRespTradeOnApprovalPromoLocationFilter_searchBrowse on SearchBrowseQueryConnection {
                facets {
                    location {
                        name
                        label
                        code
                    }
                }
            }
        `,
    }
);
